import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../../../../config';

/**
 * Shared lookup loader for tenant forms (Add + Edit). Replaces 6+ ad-hoc
 * useEffects that each fired their own POST on mount and were duplicated
 * across BrandInfo / UnitInfo / TenancyInfo.
 *
 * Independent lookups (countries, categories, delivery partners, revenue
 * methods, charges) fire in a single Promise.all on mount. Dependent
 * lookups (property by country, subcategory by category, floors by
 * property) fire when their parent id changes.
 *
 * Results are cached in a module-level Map keyed by (endpoint + payload)
 * with a 5-minute TTL so navigating Add → Edit → Detail doesn't refetch
 * the same reference data on every page mount.
 *
 * Hook contract:
 *   const lookups = useTenantLookups({ countryId, categoryId, propertyId })
 *   lookups.countries          // [{_id, country_name, ...}]
 *   lookups.properties         // filtered by countryId
 *   lookups.categories         // [{_id, category_name, ...}]
 *   lookups.subcategories      // filtered by categoryId
 *   lookups.floors             // for propertyId
 *   lookups.deliveryPartners   // [{delivery_partner_id, delivery_partner_name}]
 *   lookups.revenueMethods     // [{_id, revenue_method_id:{description,_id}}]
 *   lookups.charges            // [{other_charge_id, display_name, charge_name}]
 *   lookups.concessionaireCode // string
 *   lookups.loading            // boolean — base lookups still loading
 *   lookups.refresh()          // bust cache + reload everything
 */

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

function cacheGet(key) {
    const hit = cache.get(key);
    if (!hit) return null;
    if (Date.now() > hit.expiresAt) {
        cache.delete(key);
        return null;
    }
    return hit.data;
}

function cacheSet(key, data) {
    cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

/** Module-level cache buster — useful after creating a tenant, etc. */
export function invalidateTenantLookups() {
    cache.clear();
}

async function postCached(endpoint, payload, headers) {
    const key = `POST:${endpoint}:${JSON.stringify(payload || {})}`;
    const hit = cacheGet(key);
    if (hit) return hit;
    const res = await axios.post(endpoint, payload, { headers });
    cacheSet(key, res.data);
    return res.data;
}

async function getCached(endpoint, headers) {
    const key = `GET:${endpoint}`;
    const hit = cacheGet(key);
    if (hit) return hit;
    const res = await axios.get(endpoint, { headers });
    cacheSet(key, res.data);
    return res.data;
}

const useTenantLookups = ({ countryId, categoryId, propertyId } = {}) => {
    const concessionaireId = typeof window !== 'undefined' ? localStorage.getItem('concessionaireId') : '';
    const token = typeof window !== 'undefined' ? localStorage.getItem('sessiontoken') : '';
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [countries, setCountries] = useState([]);
    const [allProperties, setAllProperties] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allSubcategories, setAllSubcategories] = useState([]);
    const [floors, setFloors] = useState([]);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [revenueMethods, setRevenueMethods] = useState([]);
    const [charges, setCharges] = useState([]);
    const [groupNames, setGroupNames] = useState([]);
    const [concessionaireCode, setConcessionaireCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);

    const refresh = useCallback(() => {
        invalidateTenantLookups();
        setTick((t) => t + 1);
    }, []);

    // Parallel base lookups on mount + whenever cache is busted.
    useEffect(() => {
        if (!concessionaireId) return;
        let cancelled = false;
        setLoading(true);
        // allSettled (not all): some lookups legitimately 404 when the
        // concessionaire has none of that reference data (e.g. charges).
        // With Promise.all, one such rejection would skip *every* setState
        // below and blank out unrelated lookups (countries, delivery
        // partners, etc.). allSettled isolates each failure.
        Promise.allSettled([
            postCached(`${config.hostname}/country/getcountrybyconcessionaire`, { country_concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/category/getcategorybyconcessionaire`, { category_concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/subcategory/getsubcategorybyconcessionaire`, { subcategory_concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/concessionairedeliverypartner/getdeliverypartner`, { concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/concessionairerevenuemethods/getmethods`, { concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/concessionairecharges/getcharges`, { concessionaire_id: concessionaireId }, headers),
            postCached(`${config.hostname}/tenant/getgroupnames`, { tenant_concessionaire_id: concessionaireId }, headers),
            getCached(`${config.hostname}/concessionaire/${concessionaireId}`, headers),
        ])
            .then((settled) => {
                if (cancelled) return;
                // Unwrap each result; a rejected lookup falls back to null so
                // the Array.isArray guards below resolve it to an empty list.
                const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
                const [
                    countriesRes,
                    propertiesRes,
                    categoriesRes,
                    subcategoriesRes,
                    deliveryRes,
                    methodsRes,
                    chargesRes,
                    groupNamesRes,
                    concessionaireRes,
                ] = settled.map((_, i) => val(i));
                settled.forEach((r, i) => {
                    if (r.status === 'rejected') console.warn(`useTenantLookups: lookup[${i}] failed`, r.reason?.response?.status || r.reason?.message);
                });
                setCountries(Array.isArray(countriesRes) ? countriesRes : []);
                setAllProperties(Array.isArray(propertiesRes) ? propertiesRes : []);
                setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
                setAllSubcategories(Array.isArray(subcategoriesRes) ? subcategoriesRes : []);
                setDeliveryPartners(
                    (Array.isArray(deliveryRes) ? deliveryRes : []).map((item) => ({
                        delivery_partner_id: item.delivery_partner_id?._id,
                        delivery_partner_name: item.delivery_partner_id?.delivery_partner_name,
                    })),
                );
                setRevenueMethods(Array.isArray(methodsRes) ? methodsRes : []);
                setCharges(
                    (Array.isArray(chargesRes) ? chargesRes : []).map((item) => ({
                        other_charge_id: item.other_charge_id?._id,
                        display_name: item.display_name,
                        charge_name: item.other_charge_id?.charge_name,
                    })),
                );
                setGroupNames(Array.isArray(groupNamesRes) ? groupNamesRes : []);
                setConcessionaireCode(concessionaireRes?.concessionaire_code || '');
            })
            .catch((err) => {
                console.error('useTenantLookups:', err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [concessionaireId, headers, tick]);

    // Dependent: floors by selected property.
    useEffect(() => {
        if (!propertyId) {
            setFloors([]);
            return;
        }
        let cancelled = false;
        postCached(`${config.hostname}/property/getfloorsbyproperty`, { property_id: propertyId }, headers)
            .then((data) => { if (!cancelled) setFloors(Array.isArray(data) ? data : []); })
            .catch((err) => console.error('floors:', err));
        return () => { cancelled = true; };
    }, [propertyId, headers]);

    // Filtered views — derived from the base lookups so the cache hit on
    // the underlying data is reused even when the selection changes.
    const properties = useMemo(() => {
        if (!countryId) return [];
        return allProperties.filter((p) => p.property_country_id?._id === countryId || p.property_country_id === countryId);
    }, [allProperties, countryId]);

    const subcategories = useMemo(() => {
        if (!categoryId) return [];
        return allSubcategories.filter((s) => s.category_id?._id === categoryId || s.category_id === categoryId);
    }, [allSubcategories, categoryId]);

    return {
        countries,
        properties,
        allProperties,
        categories,
        subcategories,
        floors,
        deliveryPartners,
        revenueMethods,
        charges,
        groupNames,
        concessionaireCode,
        concessionaireId,
        loading,
        refresh,
    };
};

export default useTenantLookups;
